import React, { useEffect, useState } from 'react';

import { Typography, notification, Tag, Button, Modal, Checkbox, Empty } from 'antd';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';

import { DownloadOutlined } from '@ant-design/icons';

const { Title } = Typography;

import { IInvoice } from 'types/types';

import InvoiceStore from 'stores/InvoiceStore';

import SalesOrderStore from 'stores/SalesOrderStore';

import { ErrorRender } from '@partsbadger/library';

import { observer } from 'mobx-react';

import dayjs from 'dayjs';

import FileDownload from 'js-file-download';

const InvoiceDownload: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [selectedInvoices, setSelectedInvoices] = useState<CheckboxValueType[]>([]);
    const [loadingButton, setLoadingButton] = useState(false);

    const { order } = SalesOrderStore;

    const { invoices } = InvoiceStore;

    useEffect(() => {
        async function getInvoices() {
            if (order?.id) {
                await InvoiceStore.getInvoiceListByOrderId(order?.id)
                    .then()
                    .catch(error => {
                        notification.error({
                            duration: 10,
                            message: 'Error',
                            description: <ErrorRender error={error} />,
                            placement: 'topRight',
                        });

                        // setLoading(false);
                    });
            }
        }
        getInvoices();
    }, []);

    const date = dayjs(new Date()).format('YYYY-MM-DD');

    const options = invoices.map(item => {
        return {
            label: (
                <div className="flex flex-col md:flex-row md:justify-between container" style={{ maxWidth: 450 }}>
                    <div className="flex flex-col mt-3">
                        <Title level={4} className="mb-0">
                            Invoice Number: {item.name}
                        </Title>

                        <span className="mb-2">Invoice Date: {item?.invoice_date}</span>
                        <div className="flex flex-col">
                            <span>Due Date: {item?.due_date}</span>
                            {dayjs(date).isAfter(item?.due_date, 'days') && (
                                <span style={{ color: '#FA8C16' }}>
                                    (OVERDUED BY {dayjs(date).diff(item?.due_date, 'day')} DAYS)
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col md:self-center md:items-center">
                        <span className="text-lg">Status</span>
                        <div>
                            {item?.invoice_status === 'Unpaid' ? (
                                <Tag color="#FA8C16">{item?.invoice_status}</Tag>
                            ) : (
                                <Tag color="green" className="mx-auto">
                                    {item?.invoice_status}
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>
            ),
            value: item.id,
        };
    });

    const onChange = (checkedValues: CheckboxValueType[]) => {
        setSelectedInvoices(checkedValues);
    };

    const handleDownloadAllInvoices = async () => {
        if (order?.id) {
            setLoadingButton(true);
            await InvoiceStore.downloadAllInvoices(order?.id)
                .then(res => {
                    FileDownload(res, `All-Invoices-files.zip`);
                    setLoadingButton(false);
                })
                .catch(error => {
                    notification.error({
                        duration: 10,
                        message: 'Error',
                        description: <ErrorRender error={error} />,
                        placement: 'topRight',
                    });

                    setLoadingButton(false);
                });
        }
    };

    const handleDownloadSelectedInvoices = async () => {
        setLoadingButton(true);

        const params = selectedInvoices?.map(item => {
            return 'pk=' + item;
        });

        const paramString = params.join('&');

        const response = await InvoiceStore.multipleDownloadInvoice(paramString);

        if (selectedInvoices.length === 1) {
            const invoice = invoices.find(item => {
                return item.id == selectedInvoices[0];
            });
            FileDownload(response, `${invoice?.name}.pdf`);
        } else {
            FileDownload(response, `All-Invoices-files.zip`);
        }

        setLoadingButton(false);
    };

    const handleDownloadInvoice = async (id: number, name: string) => {
        setLoadingButton(true);

        await InvoiceStore.downloadInvoice(id)
            .then(res => {
                FileDownload(res, `Invoices-${name}.pdf`);
                setLoadingButton(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'topRight',
                });

                setLoadingButton(false);
            });
    };

    return (
        <>
            <Title level={2}>Invoices</Title>
            {invoices.length === 0 && (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: '#000' }}>Sorry... This order doesn't have invoices.</span>}
                />
            )}
            {invoices.length === 1 && (
                <div className="flex flex-col md:flex-row self-center justify-between" style={{ maxWidth: 900 }}>
                    <div className="flex flex-col mt-3">
                        <Title level={4}>Invoice Number: {invoices[0]?.name}</Title>
                        <span>Invoice Date: {invoices[0]?.invoice_date}</span>
                        <div className="flex flex-col">
                            <span>Due Date: {invoices[0]?.due_date}</span>
                            {invoices[0]?.invoice_status === 'Unpaid' &&
                                dayjs(date).isAfter(invoices[0]?.due_date, 'days') && (
                                    <span style={{ color: '#FA8C16' }}>
                                        (OVERDUED BY {dayjs(date).diff(invoices[0]?.due_date, 'day')} DAYS)
                                    </span>
                                )}
                        </div>
                    </div>
                    <div className="mt-1 md:self-center">
                        <span className="font-bold self-center">
                            <span className="mr-1">Status:</span>
                            {invoices[0]?.invoice_status === 'Unpaid' ? (
                                <Tag color="#FA8C16">{invoices[0]?.invoice_status}</Tag>
                            ) : (
                                <Tag color="green">{invoices[0]?.invoice_status}</Tag>
                            )}
                        </span>
                    </div>
                    <div className="mt-1 flex md:self-center">
                        <Button
                            id="place-order-btn"
                            loading={loadingButton}
                            // size="large"
                            type="primary"
                            // block={true}
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadInvoice(invoices[0]?.id, invoices[0]?.name)}
                        >
                            <span className="mr-1">Download Invoice</span>
                        </Button>
                    </div>
                </div>
            )}
            {invoices.length > 1 && (
                <div className="flex">
                    <span className="md:self-center md:mr-20">Choose your PDF invoices to download</span>
                    <div className="mt-1 flex md:self-center">
                        <Button
                            id="place-order-btn"
                            // size="large"
                            type="primary"
                            // block={true}
                            icon={<DownloadOutlined />}
                            onClick={() => setShowModal(true)}
                        >
                            <span className="mr-1">Download Invoices</span>
                        </Button>
                    </div>
                </div>
            )}

            <Modal
                title={
                    <Title level={2} className="mb-0">
                        Download Invoices
                    </Title>
                }
                open={showModal}
                onCancel={() => setShowModal(false)}
                width={520}
                footer={
                    <div className="flex flex-col md:justify-end md:flex-row">
                        <Button
                            id="place-order-btn"
                            loading={loadingButton}
                            size="large"
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadAllInvoices()}
                        >
                            <span className="mr-1">Download All Invoices</span>
                        </Button>
                        <Button
                            id="place-order-btn"
                            loading={loadingButton}
                            size="large"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadSelectedInvoices()}
                            disabled={selectedInvoices.length === 0 ? true : false}
                        >
                            Download Selected
                        </Button>
                    </div>
                }
            >
                <div style={{ overflowY: 'auto', overflowX: 'hidden', height: '58vh' }}>
                    <Checkbox.Group options={options} defaultValue={['Pear']} onChange={onChange} />
                </div>
            </Modal>
        </>
    );
};

export default observer(InvoiceDownload);
